/**
 * A money value of a table (decisions-log V91): the number in tabular figures, always with kopecks,
 * and the ISO code in a slot of one width, so in a right-aligned column whole units sit under whole
 * units, kopecks under kopecks and codes under codes. Text without a trailing code (a share, «—»)
 * renders as is. `Hours` does the same for hours: whole parts and commas in columns.
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

const NBSP = ' ';
/** The number, a space (no-break from `moneyRu`, plain from `formatMoneyCcy`) and the ISO code. */
const WITH_CODE = /^(.*)[ \u00a0]([A-Z]{3})$/;
const SLOT = 'inline-block min-w-[2.7em] whitespace-pre text-left';
/**
 * The input's border and `px-2` (1px + 8px): at rest the slot after an inline input starts right
 * after its number, as in `Amount`; while the input is focused it steps out of the box.
 */
const AFTER_INPUT = '-ml-[9px] group-has-[input:focus]/amount:ml-0';

/** The code slot: a no-break space and the code, left-aligned in a width that fits every code. */
export function CurrencyCode({ code, className }: { readonly code: string; readonly className?: string }) {
  return (
    <span className={cn(SLOT, className)}>
      {NBSP}
      {code}
    </span>
  );
}

export interface InputWithCodeProps {
  /** The inline amount input (`CellInput` or one styled like it: border, `px-2`, right-aligned). */
  readonly children: ReactNode;
  /** The ISO code, or a control in its place (the paid currency select, V57). */
  readonly code: ReactNode;
  /** Weight / colour of the code, as on the read-only rows of the column. */
  readonly className?: string;
}

/**
 * An editable amount of a money column (V91): the input and the code slot of `Amount`, so the
 * number, its kopecks and the code line up with the read-only rows of the same column when the
 * cell's right padding is theirs.
 */
export function InputWithCode({ children, code, className }: InputWithCodeProps) {
  return (
    <span className="group/amount inline-flex flex-none items-baseline">
      {children}
      <span className={cn(SLOT, AFTER_INPUT, className)}>
        {NBSP}
        {code}
      </span>
    </span>
  );
}

export interface AmountProps {
  /** Formatted value, e.g. `moneyRu(…)` / `rateValueRu(…)`: «2 279,70 BYN». */
  readonly value: string;
  readonly className?: string;
  /** At a rate that is not final yet (V197): «≈ » before the number; the cell is grey and titled by `fxEstimateTitleRu`. */
  readonly isApproximate?: boolean;
}

const APPROXIMATE = '≈ ';

export function Amount({ value, className, isApproximate }: AmountProps) {
  const match = WITH_CODE.exec(value);
  const mark = isApproximate ? APPROXIMATE : '';
  if (!match) return <span className={cn('whitespace-nowrap tabular-nums', className)}>{mark}{value}</span>;
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)}>
      {mark}
      {match[1]}
      <CurrencyCode code={match[2] ?? ''} />
    </span>
  );
}

const DECIMAL_COMMA = ',';
/** The width of «,0» in IBM Plex Sans tabular figures: a comma (0.27em) and a digit (0.6em). */
const FRACTION_SLOT = 'inline-block w-[0.88em] text-left';
/**
 * The same slot after an hours input at rest (V288): a whole value steps left by «,0», so its whole
 * part sits under the whole part of «68,5» in the row below, as in `Hours`; focused, the input takes
 * its full box at the cell's edge.
 */
const INPUT_FRACTION_SLOT = '[&:not(:focus)]:mr-[0.88em]';

/** The class of an hours input showing `value` («176», «68,5»): the empty slot when it has no fraction. */
export const hoursInputSlotClass = (value: string): string | false => !value.includes(DECIMAL_COMMA) && INPUT_FRACTION_SLOT;

export interface HoursProps {
  /** `hoursRu(…)`: «168», «117,5»; «—» renders in the place of the whole part. */
  readonly value: string;
  readonly className?: string;
}

/**
 * Hours of a table column (U 2026-10-15): the whole part right-aligned and the fraction in a slot
 * the width of «,0», so in a right-aligned column «168» and «117,5» keep their whole parts and
 * commas under each other. An hours input of the same column does the same through
 * `hoursInputSlotClass` (V288).
 */
export function Hours({ value, className }: HoursProps) {
  const [whole, fraction] = value.split(DECIMAL_COMMA);
  return (
    <span className={cn('whitespace-nowrap tabular-nums', className)}>
      {whole}
      <span className={FRACTION_SLOT}>{fraction == null ? '' : `${DECIMAL_COMMA}${fraction}`}</span>
    </span>
  );
}
